import { findUserByUsernameOrEmail, addWsIdToUser } from "./user-storage";
import { hashPassword } from "./utils";
import { logger } from "../utils/logger";
import type { CollectionRegistry } from "../types";

export interface LoginCredentials {
  username?: string;
  email?: string;
  password: string;
}

export interface ValidationResult {
  success: boolean;
  error?: string;
  data?: any;
  username?: string;
  userId?: string | number;
}

/**
 * Validate user credentials against database first, then file storage as fallback
 * @param credentials - Login credentials with username/email and password
 * @param collections - Optional collection registry for database lookup
 * @returns ValidationResult indicating success or failure
 */
export async function validateUser(
  credentials: LoginCredentials,
  collections?: CollectionRegistry
): Promise<ValidationResult> {
  const { username, email, password } = credentials;
  const identifier = username || email;

  logger.debug('[validateUser] Starting user validation');
  logger.debug(`[validateUser] Username provided: ${username ? '✓' : '✗'}, Email provided: ${email ? '✓' : '✗'}, Password provided: ${password ? '✓' : '✗'}`);

  // Check if identifier (username or email) and password are provided
  if (!identifier || !password) {
    logger.warn('[validateUser] Validation failed: Username/email and password are required');
    return {
      success: false,
      error: 'Username or email and password are required'
    };
  }

  // Try database lookup first if collections are available
  if (collections && collections['users'] && collections['users']['authenticate']) {
    logger.debug(`[validateUser] Attempting database authentication for: ${identifier}`);
    try {
      const dbResult = await collections['users']['authenticate']({
        identifier,
        password
      });

      logger.info('[validateUser] Database authentication attempt completed', dbResult);

      if (dbResult && dbResult.success && dbResult.data) {
        logger.info(`[validateUser] ✓ User authenticated via database: ${dbResult.data.username}`);
        return {
          success: true,
          data: dbResult.data.username,
          username: dbResult.data.username,
          userId: dbResult.data.id
        };
      } else {
        logger.debug(`[validateUser] Database auth failed for ${identifier}: ${dbResult?.error || 'Invalid credentials'}`);
        // Don't fall back to file if DB explicitly says invalid credentials
        if (dbResult && dbResult.error === 'Invalid credentials') {
          return {
            success: false,
            error: 'Invalid credentials'
          };
        }
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      logger.debug(`[validateUser] Database lookup error: ${errorMsg}, falling back to file storage`);
    }
  } else {
    logger.debug('[validateUser] No users collection available, using file storage only');
  }

  // Fall back to file-based validation
  logger.info(`[validateUser] Attempting file-based validation for: ${identifier}`);

  // Find user by username or email from in-memory cache
  const user = findUserByUsernameOrEmail(identifier);

  if (!user) {
    logger.warn(`[validateUser] Validation failed: User not found - ${identifier}`);
    return {
      success: false,
      error: 'Invalid username or email'
    };
  }

  logger.debug(`[validateUser] User found in file storage: ${user.username}, verifying password`);

  // Hash the stored password and compare with provided password
  const hashedPassword = hashPassword(user.password);

  if (hashedPassword !== password) {
    logger.warn(`[validateUser] Validation failed: Invalid password for user - ${user.username}`);
    logger.debug(`[validateUser] Password hash mismatch for user: ${user.username}`);
    return {
      success: false,
      error: 'Invalid password'
    };
  }

  logger.info(`[validateUser] ✓ User validated via file storage: ${user.username}`);
  return {
    success: true,
    data: user.username,
    username: user.username
  };
}

/**
 * Authenticate user and store WebSocket ID
 * Uses database first via collections, then falls back to file storage
 * @param credentials - Login credentials
 * @param wsId - WebSocket ID to store
 * @param collections - Optional collection registry for database lookup
 * @returns ValidationResult with authentication status
 */
export async function authenticateAndStoreWsId(
  credentials: LoginCredentials,
  wsId: string,
  collections?: CollectionRegistry
): Promise<ValidationResult> {
  const identifier = credentials.username || credentials.email;
  logger.debug('[authenticateAndStoreWsId] Starting authentication and WebSocket ID storage');

  // Validate user credentials first
  logger.debug('[authenticateAndStoreWsId] Validating user credentials');
  const validationResult = await validateUser(credentials, collections);

  if (!validationResult.success) {
    logger.warn(`[authenticateAndStoreWsId] User validation failed for: ${identifier}`);
    return validationResult;
  }

  // Use the username from validation result (guaranteed to exist if validation succeeded)
  const username = validationResult.username!;
  logger.info(`[authenticateAndStoreWsId] User ${username} validated, storing WebSocket ID`);

  // Store wsId in user's wsIds array using UserManager
  // Changes are automatically synced to file via setInterval
  logger.debug(`[authenticateAndStoreWsId] Calling addWsIdToUser for ${username}`);
  addWsIdToUser(username, wsId);

  logger.debug(`[authenticateAndStoreWsId] WebSocket ID ${wsId} associated with user ${username}`);
  return validationResult;
}

/**
 * Verify authentication token
 * @param authToken - Base64 encoded auth token containing username and password
 * @param collections - Optional collection registry for database lookup
 * @returns ValidationResult indicating if token is valid
 */
export async function verifyAuthToken(
  authToken: string,
  collections?: CollectionRegistry
): Promise<ValidationResult> {
  try {
    logger.debug('[verifyAuthToken] Starting token verification');

    // Decode base64 token
    logger.debug('[verifyAuthToken] Decoding base64 token');
    const decodedString = Buffer.from(authToken, 'base64').toString('utf-8');

    logger.debug('[verifyAuthToken] Parsing decoded token as JSON');
    const credentials = JSON.parse(decodedString);

    logger.debug('[verifyAuthToken] Token decoded and parsed successfully');
    logger.debug(`[verifyAuthToken] Token contains username: ${credentials.username ? '✓' : '✗'}`);

    // Validate credentials
    logger.debug('[verifyAuthToken] Validating credentials from token');
    const result = await validateUser(credentials, collections);

    if (result.success) {
      logger.info(`[verifyAuthToken] ✓ Token verified successfully for user: ${credentials.username || 'unknown'}`);
    } else {
      logger.warn(`[verifyAuthToken] Token verification failed: ${result.error}`);
    }

    return result;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error(`[verifyAuthToken] Failed to verify auth token: ${errorMsg}`);
    logger.debug('[verifyAuthToken] Token verification error details:', error);

    return {
      success: false,
      error: 'Invalid token format'
    };
  }
}
